import React, { useMemo, useState } from 'react';
import { Plus, X, GripVertical } from 'lucide-react';
import '../styles/Drawer.css';
import '../styles/DrawerForm.css';

interface ChecklistItem {
  _id?: string;
  title: string;
  description: string;
  categoryLocationGroup: string;
  isRequired: boolean;
  order: number;
}

interface InspectionType {
  _id: string;
  name: string;
  order: number;
}

interface ChecklistFormSliderProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  formData: {
    name: string;
    description: string;
    category: string;
    items: ChecklistItem[];
  };
  setFormData: React.Dispatch<React.SetStateAction<{
    name: string;
    description: string;
    category: string;
    items: ChecklistItem[];
  }>>;
  inspectionTypes: InspectionType[];
  editingChecklist: any | null;
  onAddItem: () => void;
  onUpdateItem: (index: number, field: keyof ChecklistItem, value: any) => void;
  onRemoveItem: (index: number) => void;
}

const ChecklistFormSlider: React.FC<ChecklistFormSliderProps> = ({
  isOpen,
  onClose,
  onSave,
  formData,
  setFormData,
  inspectionTypes,
  editingChecklist,
  onAddItem,
  onUpdateItem,
  onRemoveItem
}) => {
  const [draggedCategoryIndex, setDraggedCategoryIndex] = useState<number | null>(null);
  const [draggedItemIndex, setDraggedItemIndex] = useState<number | null>(null);
  const [dragOverItemIndex, setDragOverItemIndex] = useState<number | null>(null);

  // Group items by categoryLocationGroup
  const groupedItems = useMemo(() => {
    const groups: { [key: string]: { items: ChecklistItem[], indices: number[] } } = {};

    formData.items.forEach((item, index) => {
      const groupKey = item.categoryLocationGroup || '';
      if (!groups[groupKey]) {
        groups[groupKey] = { items: [], indices: [] };
      }
      groups[groupKey].items.push(item);
      groups[groupKey].indices.push(index);
    });

    return groups;
  }, [formData.items]);

  // Get unique categories in order
  const categories = useMemo(() => {
    const seen = new Set<string>();
    const result: string[] = [];

    formData.items.forEach(item => {
      const cat = item.categoryLocationGroup || '';
      if (!seen.has(cat)) {
        seen.add(cat);
        result.push(cat);
      }
    });

    return result;
  }, [formData.items]);

  if (!isOpen) return null;

  const addCategory = () => {
    // Generate a unique temporary category name
    const tempCategoryName = `New Category ${categories.length + 1}`;

    // Add a placeholder item with the new category name
    const newItem: ChecklistItem = {
      title: '',
      description: '',
      categoryLocationGroup: tempCategoryName,
      isRequired: false,
      order: formData.items.length
    };
    setFormData({
      ...formData,
      items: [...formData.items, newItem]
    });
  };

  const addItemToCategory = (categoryName: string) => {
    const newItem: ChecklistItem = {
      title: '',
      description: '',
      categoryLocationGroup: categoryName,
      isRequired: false,
      order: formData.items.length
    };
    setFormData({
      ...formData,
      items: [...formData.items, newItem]
    });
  };

  const deleteCategory = (categoryName: string) => {
    const items = formData.items.filter(item => item.categoryLocationGroup !== categoryName);
    // Update order
    items.forEach((item, i) => {
      item.order = i;
    });
    setFormData({ ...formData, items });
  };

  const updateCategoryName = (categoryIndex: number, newName: string) => {
    const categoryName = categories[categoryIndex];
    const items = formData.items.map(item => {
      if (item.categoryLocationGroup === categoryName) {
        return { ...item, categoryLocationGroup: newName };
      }
      return item;
    });
    setFormData({ ...formData, items });
  };

  // Category drag & drop handlers
  const handleCategoryDragStart = (e: React.DragEvent, categoryIndex: number) => {
    setDraggedCategoryIndex(categoryIndex);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleCategoryDragOver = (e: React.DragEvent, categoryIndex: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleCategoryDrop = (e: React.DragEvent, targetCategoryIndex: number) => {
    e.preventDefault();

    if (draggedCategoryIndex === null || draggedCategoryIndex === targetCategoryIndex) {
      setDraggedCategoryIndex(null);
      return;
    }

    const draggedCategoryName = categories[draggedCategoryIndex];
    const targetCategoryName = categories[targetCategoryIndex];

    const items = [...formData.items];
    const draggedIndices = groupedItems[draggedCategoryName]?.indices || [];
    const targetIndices = groupedItems[targetCategoryName]?.indices || [];

    // Extract the dragged category items
    const draggedItems = draggedIndices.map(idx => items[idx]);

    // Remove dragged items from array
    const itemsWithoutDragged = items.filter((_, idx) => !draggedIndices.includes(idx));

    // Find new insertion point
    const targetMin = Math.min(...targetIndices);
    const adjustedTargetIndex = draggedCategoryIndex < targetCategoryIndex
      ? targetMin - draggedIndices.length
      : targetMin;

    // Insert dragged items at new position
    itemsWithoutDragged.splice(adjustedTargetIndex, 0, ...draggedItems);

    // Update order
    itemsWithoutDragged.forEach((item, i) => {
      item.order = i;
    });

    setFormData({ ...formData, items: itemsWithoutDragged });
    setDraggedCategoryIndex(null);
  };

  // Item drag & drop handlers
  const handleItemDragStart = (e: React.DragEvent, itemIndex: number) => {
    setDraggedItemIndex(itemIndex);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleItemDragOver = (e: React.DragEvent, itemIndex: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverItemIndex(itemIndex);
  };

  const handleItemDragLeave = () => {
    setDragOverItemIndex(null);
  };

  const handleItemDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    setDragOverItemIndex(null);

    if (draggedItemIndex === null || draggedItemIndex === targetIndex) {
      setDraggedItemIndex(null);
      return;
    }

    const items = [...formData.items];
    const draggedItem = items[draggedItemIndex];

    // Remove item from old position
    items.splice(draggedItemIndex, 1);

    // Insert at new position
    const adjustedTargetIndex = draggedItemIndex < targetIndex ? targetIndex - 1 : targetIndex;
    items.splice(adjustedTargetIndex, 0, draggedItem);

    // Update order
    items.forEach((item, i) => {
      item.order = i;
    });

    setFormData({ ...formData, items });
    setDraggedItemIndex(null);
  };

  return (
    <>
      {/* Drawer Overlay */}
      <div className="drawer-overlay" onClick={onClose} />

      {/* Drawer Container */}
      <div className="drawer">
        {/* Drawer Header */}
        <div className="drawer-header">
          <h2 className="drawer-title">
            {editingChecklist ? 'Edit Checklist' : 'New Checklist'}
          </h2>
          <button className="drawer-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Drawer Content */}
        <div className="drawer-content" style={{ padding: '24px' }}>
          <div className="drawer-form-content">
            {/* Basic Information Section - No Card */}
            <div>
              {/* Name and Inspection Type in same row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter checklist name"
                  />
                </div>

                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Inspection Type *</label>
                  <select
                    className="form-input"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    <option value="">Select Inspection Type</option>
                    {inspectionTypes.map((type) => (
                      <option key={type._id} value={type._id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="form-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter description"
                  rows={6}
                  style={{ minHeight: '120px' }}
                />
              </div>
            </div>

            {/* Items Section - Header */}
            <div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px'
              }}>
                <h3 style={{
                  fontSize: '15px',
                  fontWeight: '600',
                  color: '#111827',
                  margin: 0
                }}>
                  Check Items
                </h3>
                <button
                  onClick={addCategory}
                  className="btn btn-secondary btn-sm"
                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  <Plus size={16} />
                  Add Category
                </button>
              </div>

              {formData.items.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '20px',
                  backgroundColor: '#F9FAFB',
                  borderRadius: '6px',
                  color: '#6B7280',
                  fontSize: '14px'
                }}>
                  No items added yet
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {categories.map((categoryName, categoryIndex) => {
                    const { items, indices } = groupedItems[categoryName];

                    return (
                      <div
                        key={`category-${categoryIndex}`}
                        className="drawer-form-section"
                        draggable
                        onDragStart={(e) => handleCategoryDragStart(e, categoryIndex)}
                        onDragOver={(e) => handleCategoryDragOver(e, categoryIndex)}
                        onDrop={(e) => handleCategoryDrop(e, categoryIndex)}
                        style={{
                          opacity: draggedCategoryIndex === categoryIndex ? 0.5 : 1,
                          cursor: 'move'
                        }}
                      >
                        {/* Category Header */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '12px'
                        }}>
                          {/* Drag Handle */}
                          <div style={{
                            cursor: 'grab',
                            color: '#9CA3AF',
                            display: 'flex',
                            alignItems: 'center',
                            paddingTop: '22px'
                          }}>
                            <GripVertical size={20} />
                          </div>

                          <div style={{ flex: 1 }}>
                            <label className="form-label">
                              Category / Location / Group:
                            </label>
                            <input
                              type="text"
                              className="form-input"
                              value={categoryName}
                              onChange={(e) => updateCategoryName(categoryIndex, e.target.value)}
                              placeholder="Enter category, location, or group name"
                              style={{ marginBottom: 0 }}
                            />
                          </div>

                          {/* Delete Category Button */}
                          <button
                            onClick={() => deleteCategory(categoryName)}
                            style={{
                              border: '1px solid #FCA5A5',
                              background: '#FFFFFF',
                              cursor: 'pointer',
                              padding: '6px',
                              borderRadius: '6px',
                              color: '#EF4444',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginTop: '22px'
                            }}
                            title="Delete category"
                          >
                            <X size={16} />
                          </button>
                        </div>

                        {/* Check Items */}
                        <div className="form-group" style={{ marginLeft: '28px' }}>
                          <label className="form-label">
                            Check items:
                          </label>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {items.map((item, itemIndex) => {
                              const actualIndex = indices[itemIndex];
                              return (
                                <div
                                  key={actualIndex}
                                  draggable
                                  onDragStart={(e) => handleItemDragStart(e, actualIndex)}
                                  onDragOver={(e) => handleItemDragOver(e, actualIndex)}
                                  onDragLeave={handleItemDragLeave}
                                  onDrop={(e) => handleItemDrop(e, actualIndex)}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    opacity: draggedItemIndex === actualIndex ? 0.5 : 1,
                                    backgroundColor: dragOverItemIndex === actualIndex ? '#F3F4F6' : 'transparent',
                                    padding: dragOverItemIndex === actualIndex ? '4px' : '0',
                                    borderRadius: '6px',
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  {/* Drag Handle */}
                                  <div style={{
                                    cursor: 'grab',
                                    color: '#9CA3AF',
                                    display: 'flex',
                                    alignItems: 'center'
                                  }}>
                                    <GripVertical size={16} />
                                  </div>

                                  <input
                                    type="text"
                                    className="form-input"
                                    value={item.title}
                                    onChange={(e) => onUpdateItem(actualIndex, 'title', e.target.value)}
                                    placeholder="Item title"
                                    style={{
                                      flex: 1,
                                      marginBottom: 0
                                    }}
                                  />

                                  {/* Delete Button */}
                                  <button
                                    onClick={() => onRemoveItem(actualIndex)}
                                    style={{
                                      border: '1px solid #FCA5A5',
                                      background: '#FFFFFF',
                                      cursor: 'pointer',
                                      padding: '6px',
                                      borderRadius: '6px',
                                      color: '#EF4444',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                    title="Delete item"
                                  >
                                    <X size={16} />
                                  </button>
                                </div>
                              );
                            })}

                            {/* Add Item to Category Button */}
                            <button
                              onClick={() => addItemToCategory(categoryName)}
                              style={{
                                border: '1px dashed #D1D5DB',
                                background: 'transparent',
                                cursor: 'pointer',
                                padding: '8px',
                                borderRadius: '6px',
                                color: '#6B7280',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '13px',
                                fontWeight: '500',
                                marginTop: '4px'
                              }}
                              title="Add item to this category"
                            >
                              <Plus size={14} style={{ marginRight: '4px' }} />
                              Add Item
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Drawer Footer */}
        <div className="drawer-footer">
          <button
            onClick={onClose}
            className="btn btn-secondary btn-md"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="btn btn-primary btn-md"
          >
            {editingChecklist ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </>
  );
};

export default ChecklistFormSlider;
